import React, { useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useSessionManager from '@afosto/auth-react/useSessionManager';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import EditProfileForm from '../../../components/EditProfileForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import gravatarUrl from '@afosto/utils/gravatarUrl';
import { users as usersApi } from '@afosto/iam/api/Users';
import translations from './translations';

const ProfileInformation = () => {
  const [hasPermissions] = usePermissions(['iam:users:write']);
  const intl = useIntl();
  const sessionManager = useSessionManager();
  const { user } = sessionManager ?? {};
  const { enqueueSnackbar } = useSnackbar();
  const [showEditDrawer, setShowEditDrawer] = useState(false);

  /**
   * Handle the closing of the drawer
   */
  const handleCloseEditDrawer = () => setShowEditDrawer(false);

  /**
   * Handle the opening of the drawer
   */
  const handleOpenEditDrawer = () => setShowEditDrawer(true);

  /**
   * Handle the onSubmit event of the edit form.
   * @param {Array} roles
   * @param {String} firstName
   * @param {String} lastName
   * @returns {Promise}
   */
  const handleSaveUser = async ({ firstName, lastName }) => {
    try {
      await usersApi.updateUser(user?.id, {
        first_name: firstName,
        last_name: lastName,
      });
      await sessionManager.silentAuthorization();
      handleCloseEditDrawer();
      enqueueSnackbar(intl.formatMessage(translations.feedback.profileSaved), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.profileNotSaved), {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Card>
        <CardContent>
          <Grid container>
            <Grid item style={{ flex: '1 0 0' }}>
              <List disablePadding dense>
                <ListItem disableGutters>
                  <ListItemAvatar>
                    <Avatar src={user && gravatarUrl(user.email, { letterValue: user.name })} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={user?.name}
                    primaryTypographyProps={{ variant: 'h6' }}
                    secondary={user?.email}
                  />
                </ListItem>
              </List>
            </Grid>
            {hasPermissions() && (
              <Grid item>
                <Button variant="outlined" color="default" onClick={handleOpenEditDrawer}>
                  <FormattedMessage {...translations.actions.edit} />
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
      <FormDrawer
        isOpen={showEditDrawer}
        onClose={handleCloseEditDrawer}
        title={intl.formatMessage(translations.editDrawer.title)}
        drawerWidth="400px"
      >
        <EditProfileForm onSubmit={handleSaveUser} onCancel={handleCloseEditDrawer} user={user} />
      </FormDrawer>
    </>
  );
};

export default ProfileInformation;
